import { useState, useRef, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { 
  QrCode, Download, Copy, Check, Settings2, 
  Share2, Link2, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * SmartLinkPanel - QR Code and Smart Link management for doctors
 */
const SmartLinkPanel = ({ user, isOpen, onClose }) => {
  const [smartLinkData, setSmartLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allInstitutionLinks, setAllInstitutionLinks] = useState([]);
  const qrRef = useRef(null);
  
  const [formData, setFormData] = useState({
    slug: "",
    public_name: "",
    bio: ""
  });

  // Load smart link data
  useEffect(() => {
    if (isOpen && user) {
      loadSmartLinkData();
    }
  }, [isOpen, user]);

  const loadSmartLinkData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      // Get user's institution
      const instResponse = await fetch(`${API_URL}/api/institutions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!instResponse.ok) throw new Error("Error loading institution");
      
      const institutions = await instResponse.json();
      const userInstitution = institutions.find(i => i.id === user.institution_id);
      
      // Build one card per tenant the doctor is linked to.
      // Private practice tenants are included even when the doctor has NOT activated
      // them, so we can render a "prompt to activate" CTA instead of silently hiding them.
      const instIds = user.institution_ids && user.institution_ids.length > 0 
        ? user.institution_ids 
        : [user.institution_id];
      const links = [];
      for (const inst of institutions) {
        if (!instIds.includes(inst.id) || !inst.slug) continue;
        const isPrivate = !!inst.is_private_practice;
        const activated = isPrivate ? !!user.private_practice_active : true;
        // Only build the actual URL when the doctor has a slug configured and the
        // tenant is usable. For non-activated private practice we still include the
        // entry to display the CTA card.
        const fullUrl = (user.slug && activated) ? `${window.location.origin}/${inst.slug}/${user.slug}` : null;
        links.push({
          institution: inst,
          fullUrl,
          isPrivatePractice: isPrivate,
          activated,
        });
      }
      // Sort: main (non-private) tenants first, private practice at the end.
      links.sort((a, b) => (a.isPrivatePractice ? 1 : 0) - (b.isPrivatePractice ? 1 : 0));
      setAllInstitutionLinks(links);
      
      if (userInstitution && userInstitution.slug && user.slug) {
        // User has smart link configured
        setSmartLinkData({
          institution: userInstitution,
          doctor: user,
          fullUrl: `${window.location.origin}/${userInstitution.slug}/${user.slug}`
        });
        setFormData({
          slug: user.slug || "",
          public_name: user.public_name || user.name || "",
          bio: user.bio || ""
        });
      } else {
        // Smart link not configured
        setSmartLinkData({
          institution: userInstitution,
          doctor: user,
          fullUrl: null,
          notConfigured: true
        });
        setFormData({
          slug: generateSlug(user.name || ""),
          public_name: user.name || "",
          bio: ""
        });
      }
    } catch (error) {
      console.error("Error loading smart link data:", error);
      toast.error("Error al cargar datos del enlace");
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (text) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/[\s]+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleSave = async () => {
    if (!formData.slug.trim()) {
      toast.error("El slug es requerido");
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('auth_token');
      
      const params = new URLSearchParams();
      params.append('slug', formData.slug.trim());
      params.append('public_name', formData.public_name.trim());
      params.append('bio', formData.bio.trim());
      
      const response = await fetch(`${API_URL}/api/doctors/${user.id}/smart-link?${params.toString()}`, {
        method: 'PUT',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Error al guardar");
      }
      
      // Update user object in local state so the panel refreshes correctly
      if (user) {
        user.slug = formData.slug.trim();
        user.public_name = formData.public_name.trim();
        user.bio = formData.bio.trim();
      }
      
      toast.success("Configuración guardada");
      setEditing(false);
      loadSmartLinkData();
    } catch (error) {
      console.error("Error saving:", error);
      toast.error(error.message || "Error al guardar configuración");
    } finally {
      setSaving(false);
    }
  };

  const handleCopyLink = async () => {
    if (!smartLinkData?.fullUrl) return;
    
    try {
      await navigator.clipboard.writeText(smartLinkData.fullUrl);
      setCopied(true);
      toast.success("Enlace copiado");
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      toast.error("Error al copiar");
    }
  };

  const handleDownloadQR = () => {
    if (!qrRef.current) return;
    
    const svg = qrRef.current.querySelector('svg');
    if (!svg) return;
    
    // Create a canvas to convert SVG to PNG
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    
    // Set canvas size (larger for better quality)
    canvas.width = 512;
    canvas.height = 512;
    
    img.onload = () => {
      // White background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Download
      const link = document.createElement('a');
      link.download = `qr-${smartLinkData.doctor.slug || 'doctor'}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      
      toast.success("QR descargado");
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  if (loading) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-lg">
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  const hasSmartLink = smartLinkData?.fullUrl && !smartLinkData?.notConfigured;
  // "Multi-tenant" means the doctor has 2+ tenants OR there is at least one tenant
  // whose link is not yet available (e.g. Consultorio Privado disabled), which we
  // still want to surface as a CTA card. In these cases we ditch the single-big-QR
  // layout and render one card per tenant to eliminate the "which link am I copying?"
  // ambiguity that caused consults to land on the wrong inbox.
  const activatedLinksCount = allInstitutionLinks.filter(l => l.fullUrl).length;
  const hasInactivePrivatePractice = allInstitutionLinks.some(l => l.isPrivatePractice && !l.activated);
  const useMultiTenantLayout = activatedLinksCount >= 2 || hasInactivePrivatePractice;

  const copyLink = async (url, label) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`Enlace de ${label} copiado`);
    } catch (e) {
      toast.error('Error al copiar');
    }
  };

  // Download the QR for an arbitrary tenant card. We look up the SVG rendered
  // inside the corresponding card by data-qr-slug so each institution can be
  // downloaded independently (needed once the doctor is on multiple tenants —
  // e.g. Fleni + Consultorio Privado — and wants to print one for the front
  // desk of each institution's secretaría).
  const downloadTenantQR = (link) => {
    const container = document.querySelector(`[data-qr-slug="${link.institution.slug}"]`);
    const svg = container?.querySelector("svg");
    if (!svg) {
      toast.error("No se pudo generar el QR");
      return;
    }
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const svgData = new XMLSerializer().serializeToString(svg);
    const img = new Image();
    canvas.width = 512;
    canvas.height = 512;
    img.onload = () => {
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      const a = document.createElement('a');
      a.download = `qr-${link.institution.slug}-${(smartLinkData?.doctor?.slug || 'doctor')}.png`;
      a.href = canvas.toDataURL('image/png');
      a.click();
      toast.success("QR descargado");
    };
    img.onerror = () => toast.error("No se pudo generar el QR");
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl overflow-hidden p-0">
        <div className="max-h-[85vh] overflow-y-auto px-6 py-6">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <QrCode className="w-5 h-5 text-blue-600" />
            Mi Enlace Personalizado
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-5 pt-2">
          {/* QR Code Section */}
          {hasSmartLink && !useMultiTenantLayout && (
            <>
              <div className="flex flex-col items-center">
                <div 
                  ref={qrRef}
                  className="bg-white p-3 rounded-xl border-2 border-slate-200 shadow-sm"
                >
                  <QRCodeSVG 
                    value={smartLinkData.fullUrl}
                    size={160}
                    level="H"
                    includeMargin={true}
                  />
                </div>
                
                <p className="mt-2 text-sm text-slate-600 text-center">
                  {smartLinkData.doctor.public_name || smartLinkData.doctor.name}
                </p>
                
                {/* Download QR Button */}
                <Button 
                  variant="outline" 
                  size="sm" 
                  className="mt-2"
                  onClick={handleDownloadQR}
                  data-testid="download-qr-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Descargar QR
                </Button>
              </div>

              {/* Link Display */}
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <Label className="text-xs text-slate-500 mb-1.5 block">Tu enlace personalizado</Label>
                <div className="bg-white rounded-md border border-slate-200 px-2 py-1.5 text-xs font-mono text-slate-700 truncate">
                  {smartLinkData.fullUrl}
                </div>

                {/* Primary Copy Button - Prominent */}
                <Button 
                  className="w-full mt-2.5 bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleCopyLink}
                  data-testid="copy-link-btn"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      ¡Enlace copiado!
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4 mr-2" />
                      Copiar enlace para compartir
                    </>
                  )}
                </Button>
              </div>

              {/* Actions */}
              <Button 
                variant="outline" 
                className="w-full"
                onClick={() => setEditing(!editing)}
                data-testid="edit-smart-link-btn"
              >
                <Settings2 className="w-4 h-4 mr-2" />
                {editing ? "Cancelar" : "Editar perfil"}
              </Button>
            </>
          )}

          {/* Multi-tenant grid: one card per tenant. Each card has its own QR,
              copy button and clearly labels which inbox that link routes to.
              Private-practice tenants not yet activated render a CTA card. */}
          {hasSmartLink && useMultiTenantLayout && (
            <>
              <p className="text-sm text-slate-600 -mt-1">
                Compartí un enlace distinto según dónde querés recibir la consulta.
                Cada enlace envía a una bandeja diferente.
              </p>
              <div className="space-y-3" data-testid="multi-tenant-links">
                {allInstitutionLinks.map((link) => {
                  const primaryColor = link.institution.branding?.primary_color || '#1d366b';
                  // CTA card for private practice that the doctor has not activated yet.
                  if (link.isPrivatePractice && !link.activated) {
                    return (
                      <div
                        key={link.institution.id}
                        className="rounded-xl border-2 border-dashed border-slate-300 bg-slate-50 p-4"
                        data-testid={`tenant-card-${link.institution.slug}-inactive`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#cbd5e1' }}>
                            <Link2 className="h-5 w-5 text-white" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-slate-800">{link.institution.name}</p>
                            <p className="text-xs text-slate-500 mt-0.5">
                              Todavía no activaste tu Consultorio Privado. Activalo para obtener tu enlace propio y recibir consultas en una bandeja separada.
                            </p>
                            <Button
                              variant="outline"
                              size="sm"
                              className="mt-2"
                              onClick={() => { onClose?.(); if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('open-private-practice-panel')); }}
                              data-testid={`activate-${link.institution.slug}-btn`}
                            >
                              Activar Consultorio Privado
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={link.institution.id}
                      className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
                      data-testid={`tenant-card-${link.institution.slug}`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="bg-white p-1.5 rounded-lg border border-slate-200 flex-shrink-0"
                          data-qr-slug={link.institution.slug}
                        >
                          <QRCodeSVG value={link.fullUrl} size={72} level="M" includeMargin={false} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: primaryColor }} />
                            <p className="font-semibold text-slate-900 truncate">{link.institution.name}</p>
                          </div>
                          <p className="text-xs text-slate-400 mb-1">
                            Las consultas de este enlace caen en la bandeja de <strong>{link.institution.name}</strong>.
                          </p>
                          <div className="bg-slate-50 rounded-md border border-slate-200 px-2 py-1 text-[11px] font-mono text-slate-700 truncate">
                            {link.fullUrl}
                          </div>
                          <div className="flex gap-2 mt-2">
                            <Button
                              className="flex-1 h-8 text-xs text-white"
                              style={{ backgroundColor: primaryColor }}
                              onClick={() => copyLink(link.fullUrl, link.institution.name)}
                              data-testid={`copy-link-${link.institution.slug}`}
                            >
                              <Copy className="w-3.5 h-3.5 mr-1.5" />
                              Copiar enlace
                            </Button>
                            <Button
                              variant="outline"
                              className="h-8 text-xs px-3"
                              onClick={() => downloadTenantQR(link)}
                              data-testid={`download-qr-${link.institution.slug}`}
                              title="Descargar QR para imprimir"
                            >
                              <Download className="w-3.5 h-3.5 mr-1.5" />
                              QR
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <Button
                variant="outline"
                className="w-full"
                onClick={() => setEditing(!editing)}
                data-testid="edit-smart-link-btn"
              >
                <Settings2 className="w-4 h-4 mr-2" />
                {editing ? "Cancelar" : "Editar perfil"}
              </Button>
            </>
          )}

          {!hasSmartLink && (
            /* Not Configured State */
            <div className="text-center py-6">
              <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Link2 className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-2">
                Configura tu enlace personalizado
              </h3>
              <p className="text-sm text-slate-500 mb-4">
                Crea una URL única para compartir con tus pacientes y generar un código QR.
              </p>
              
              {!smartLinkData?.institution?.slug && (
                <div className="flex items-center gap-2 bg-amber-50 text-amber-700 text-sm p-3 rounded-lg mb-4">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <span>Tu institución necesita configurar un slug primero. Contacta al administrador.</span>
                </div>
              )}
              
              <Button 
                onClick={() => setEditing(true)}
                disabled={!smartLinkData?.institution?.slug}
                data-testid="setup-smart-link-btn"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Configurar enlace
              </Button>
            </div>
          )}

          {/* Edit Form */}
          {editing && (
            <div className="space-y-4 pt-4 border-t border-slate-200">
              <div>
                <Label htmlFor="slug">URL personalizada *</Label>
                <div className="flex items-center mt-1">
                  <span className="text-sm text-slate-500 bg-slate-100 px-3 py-2 rounded-l-md border border-r-0 border-slate-200">
                    /{smartLinkData?.institution?.slug || 'institucion'}/
                  </span>
                  <Input
                    id="slug"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: generateSlug(e.target.value)})}
                    placeholder="tu-nombre"
                    className="rounded-l-none"
                    data-testid="slug-input"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Solo letras, números y guiones
                </p>
              </div>

              <div>
                <Label htmlFor="public_name">Nombre público</Label>
                <Input
                  id="public_name"
                  value={formData.public_name}
                  onChange={(e) => setFormData({...formData, public_name: e.target.value})}
                  placeholder="Dr. Juan Pérez"
                  className="mt-1"
                  data-testid="public-name-input"
                />
              </div>

              <div>
                <Label htmlFor="bio">Descripción breve</Label>
                <Textarea
                  id="bio"
                  value={formData.bio}
                  onChange={(e) => setFormData({...formData, bio: e.target.value})}
                  placeholder="Médico especialista en..."
                  rows={3}
                  className="mt-1"
                  data-testid="bio-input"
                />
              </div>

              <div className="flex gap-2 pt-2">
                <Button 
                  variant="outline" 
                  className="flex-1"
                  onClick={() => setEditing(false)}
                >
                  Cancelar
                </Button>
                <Button 
                  className="flex-1"
                  onClick={handleSave}
                  disabled={saving}
                  data-testid="save-smart-link-btn"
                >
                  {saving ? "Guardando..." : "Guardar"}
                </Button>
              </div>
            </div>
          )}

          {/* Tips */}
          {hasSmartLink && !editing && (
            <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
              <h4 className="text-sm font-medium text-blue-900 mb-1.5">💡 Tips para compartir</h4>
              <ul className="text-xs text-blue-700 space-y-0.5">
                <li>• Imprime el QR para tu consultorio</li>
                <li>• Agrégalo a tu firma de email</li>
                <li>• Compártelo por WhatsApp con pacientes</li>
              </ul>
            </div>
          )}
        </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SmartLinkPanel;
